import React, { useState, useEffect } from 'react';
import Layout from '@/components/Layout';
import { useAuth } from '@/context/AuthContext';
import { getAgents, createAgent, updateAgent, deleteAgent } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Plus, 
  Loader2, 
  Search, 
  Edit2, 
  Trash2,
  User,
  Building,
  Phone,
  Mail,
  FileText
} from 'lucide-react';
import { toast } from 'sonner';

const AgentsPage = () => {
  const { canManageTasks, isSuperadmin } = useAuth();
  
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState('internal');
  
  // Agent dialog
  const [showDialog, setShowDialog] = useState(false);
  const [editingAgent, setEditingAgent] = useState(null);
  const [agentForm, setAgentForm] = useState({
    name: '',
    company: '',
    position: '',
    agent_type: 'internal',
    phone: '',
    email: '',
    notes: '',
  });
  const [saving, setSaving] = useState(false);
  
  // Delete dialog
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deletingAgent, setDeletingAgent] = useState(null);

  useEffect(() => {
    loadAgents();
  }, [search]);

  const loadAgents = async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      
      const response = await getAgents(params);
      setAgents(response.data);
    } catch (error) {
      console.error('Error loading agents:', error);
      toast.error('Error al cargar los agentes');
    } finally {
      setLoading(false);
    }
  };

  const openCreateDialog = (type) => {
    setEditingAgent(null);
    setAgentForm({
      name: '',
      company: type === 'internal' ? 'UGOLF ARAVELL' : '',
      position: '',
      agent_type: type,
      phone: '',
      email: '',
      notes: '',
    });
    setShowDialog(true);
  };

  const openEditDialog = (agent) => {
    setEditingAgent(agent);
    setAgentForm({
      name: agent.name,
      company: agent.company || '',
      position: agent.position || '',
      agent_type: agent.agent_type,
      phone: agent.phone || '',
      email: agent.email || '',
      notes: agent.notes || '',
    });
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!agentForm.name.trim()) {
      toast.error('El nombre es requerido');
      return;
    }

    setSaving(true);
    try {
      if (editingAgent) {
        await updateAgent(editingAgent.id, agentForm);
        toast.success('Agente actualizado');
      } else {
        await createAgent(agentForm);
        toast.success('Agente creado');
      }
      setShowDialog(false);
      loadAgents();
    } catch (error) {
      console.error('Error saving agent:', error);
      toast.error(error.response?.data?.detail || 'Error al guardar el agente');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteAgent(deletingAgent.id);
      toast.success('Agente eliminado');
      setShowDeleteDialog(false);
      loadAgents();
    } catch (error) {
      console.error('Error deleting agent:', error);
      toast.error('Error al eliminar el agente');
    }
  };

  const internalAgents = agents.filter(a => a.agent_type === 'internal');
  const externalAgents = agents.filter(a => a.agent_type === 'external');

  const renderAgentCard = (agent) => (
    <Card 
      key={agent.id} 
      className="bg-white border border-stone-200 shadow-sm hover:shadow-md transition-shadow"
      data-testid={`agent-card-${agent.id}`}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
              agent.agent_type === 'internal' ? 'bg-emerald-100' : 'bg-blue-100'
            }`}>
              {agent.agent_type === 'internal' ? (
                <User className="w-5 h-5 text-emerald-600" />
              ) : (
                <Building className="w-5 h-5 text-blue-600" />
              )}
            </div>
            <div>
              <h3 className="font-semibold text-slate-900">{agent.name}</h3>
              {agent.position && (
                <p className="text-sm text-slate-500">{agent.position}</p>
              )}
              {agent.company && agent.agent_type === 'external' && (
                <p className="text-sm text-slate-400">{agent.company}</p>
              )}
              
              <div className="flex flex-wrap gap-3 mt-2">
                {agent.phone && (
                  <span className="flex items-center gap-1 text-xs text-slate-500">
                    <Phone className="w-3 h-3" />
                    {agent.phone}
                  </span>
                )}
                {agent.email && (
                  <span className="flex items-center gap-1 text-xs text-slate-500">
                    <Mail className="w-3 h-3" />
                    {agent.email}
                  </span>
                )}
              </div>
              
              {agent.notes && (
                <p className="text-xs text-slate-400 mt-2 flex items-start gap-1">
                  <FileText className="w-3 h-3 mt-0.5 flex-shrink-0" />
                  <span className="line-clamp-2">{agent.notes}</span>
                </p>
              )}
            </div>
          </div>
          
          {canManageTasks() && !agent.user_id && (
            <div className="flex gap-1">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => openEditDialog(agent)}
                className="h-8 w-8 text-slate-400 hover:text-slate-600"
                data-testid={`edit-agent-${agent.id}`}
              >
                <Edit2 className="w-4 h-4" />
              </Button>
              {isSuperadmin() && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => {
                    setDeletingAgent(agent);
                    setShowDeleteDialog(true);
                  }}
                  className="h-8 w-8 text-slate-400 hover:text-red-600"
                  data-testid={`delete-agent-${agent.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              )}
            </div>
          )}
          
          {agent.user_id && (
            <Badge className="bg-slate-100 text-slate-600 text-[10px]">
              Usuario del Sistema
            </Badge>
          )}
        </div>
      </CardContent>
    </Card>
  );

  return (
    <Layout>
      <div className="p-6 md:p-10" data-testid="agents-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-semibold text-slate-900 font-['Manrope'] tracking-tight">
              Agentes
            </h1>
            <p className="text-slate-500 mt-1">Gestiona los agentes internos y externos</p>
          </div>
        </div>

        {/* Search */}
        <Card className="mb-6 bg-white border border-stone-200 shadow-sm">
          <CardContent className="p-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                placeholder="Buscar por nombre o empresa..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 border-stone-200"
                data-testid="search-agents-input"
              />
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <div className="flex items-center justify-between mb-4">
            <TabsList className="bg-stone-100">
              <TabsTrigger value="internal" className="data-[state=active]:bg-white">
                <User className="w-4 h-4 mr-2" />
                Internos ({internalAgents.length})
              </TabsTrigger>
              <TabsTrigger value="external" className="data-[state=active]:bg-white">
                <Building className="w-4 h-4 mr-2" />
                Externos ({externalAgents.length})
              </TabsTrigger>
            </TabsList>
            
            {canManageTasks() && (
              <Button
                onClick={() => openCreateDialog(activeTab)}
                className="bg-slate-900 hover:bg-slate-800"
                data-testid="new-agent-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Nuevo Agente
              </Button>
            )}
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
            </div>
          ) : (
            <>
              <TabsContent value="internal" className="mt-0">
                {internalAgents.length === 0 ? (
                  <Card className="bg-white border border-stone-200">
                    <CardContent className="flex flex-col items-center justify-center h-48 text-slate-500">
                      <User className="w-12 h-12 mb-3 text-slate-300" />
                      <p>No hay agentes internos</p>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {internalAgents.map(renderAgentCard)}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="external" className="mt-0">
                {externalAgents.length === 0 ? (
                  <Card className="bg-white border border-stone-200">
                    <CardContent className="flex flex-col items-center justify-center h-48 text-slate-500">
                      <Building className="w-12 h-12 mb-3 text-slate-300" />
                      <p>No hay agentes externos</p>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {externalAgents.map(renderAgentCard)}
                  </div>
                )}
              </TabsContent>
            </>
          )}
        </Tabs>
      </div>

      {/* Agent Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-lg" data-testid="agent-dialog">
          <DialogHeader>
            <DialogTitle>
              {editingAgent ? 'Editar Agente' : 'Nuevo Agente'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Nombre *</Label>
              <Input
                value={agentForm.name}
                onChange={(e) => setAgentForm({ ...agentForm, name: e.target.value })}
                placeholder="Nombre completo"
                className="border-stone-200"
                data-testid="agent-name-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Tipo</Label>
              <Select 
                value={agentForm.agent_type} 
                onValueChange={(v) => setAgentForm({ ...agentForm, agent_type: v })}
                disabled={editingAgent !== null}
              >
                <SelectTrigger className="border-stone-200">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="internal">Interno</SelectItem>
                  <SelectItem value="external">Externo</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Empresa</Label>
                <Input
                  value={agentForm.company}
                  onChange={(e) => setAgentForm({ ...agentForm, company: e.target.value })}
                  placeholder="Nombre de la empresa"
                  className="border-stone-200"
                />
              </div>
              <div className="space-y-2">
                <Label>Cargo/Posicion</Label>
                <Input
                  value={agentForm.position}
                  onChange={(e) => setAgentForm({ ...agentForm, position: e.target.value })}
                  placeholder="Cargo o posicion"
                  className="border-stone-200"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Telefono</Label>
                <Input
                  value={agentForm.phone}
                  onChange={(e) => setAgentForm({ ...agentForm, phone: e.target.value })}
                  placeholder="+34 XXX XXX XXX"
                  className="border-stone-200"
                />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={agentForm.email}
                  onChange={(e) => setAgentForm({ ...agentForm, email: e.target.value })}
                  placeholder="email@ejemplo.com"
                  className="border-stone-200"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Notas</Label>
              <Textarea
                value={agentForm.notes}
                onChange={(e) => setAgentForm({ ...agentForm, notes: e.target.value })}
                placeholder="Notas adicionales..."
                rows={3}
                className="border-stone-200"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleSave} 
              disabled={saving}
              className="bg-slate-900 hover:bg-slate-800"
              data-testid="save-agent-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {editingAgent ? 'Guardar' : 'Crear'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar Agente</AlertDialogTitle>
            <AlertDialogDescription>
              Esta accion no se puede deshacer. Se eliminara permanentemente el agente 
              <strong> {deletingAgent?.name}</strong>.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
};

export default AgentsPage;
